import { Link } from 'react-router-dom';
import type { ReviewType } from '@wineryxplorer/shared';
import { trpc } from '../lib/trpc';
import { PageContainer } from '../components/PageContainer';
import { Typography } from '../components/ui/Typography';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ReviewCard, type AnySerializedReview } from '../components/reviews/ReviewCard';

// reviewType → entity detail route base.
const ENTITY_ROUTE: Record<ReviewType, string> = {
  venue: '/wineries',
  wine: '/wines',
  event: '/events',
  experience: '/tasting-experiences',
};

// reviewType → human label shown beside the entity name.
const ENTITY_LABEL: Record<ReviewType, string> = {
  venue: 'Winery',
  wine: 'Wine',
  event: 'Event',
  experience: 'Tasting Experience',
};

export const LatestReviews = () => {
  const { data, isLoading, error } = trpc.reviewFeed.latest.useQuery();
  const items = data?.items ?? [];

  return (
    <div className="px-0 md:px-4 pt-2 pb-4">
      <PageContainer>
        <Typography variant="heading1">Latest Reviews</Typography>
        <Typography variant="bodyMedium" className="mt-1 mb-6 text-stone-600">
          The newest reviews from across WineryXplorer — wineries, wines, events, and tasting
          experiences.
        </Typography>

        {isLoading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        )}

        {error && !isLoading && (
          <p className="py-12 text-center text-red-600">
            Couldn&apos;t load the latest reviews. Please try again later.
          </p>
        )}

        {!isLoading && !error && items.length === 0 && (
          <p className="py-12 text-center text-stone-500">No reviews yet.</p>
        )}

        {!isLoading && !error && items.length > 0 && (
          <div className="space-y-8">
            {items.map((item) => (
              <article key={`${item.reviewType}-${item.review._id}`}>
                <div className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <Link
                    to={`${ENTITY_ROUTE[item.reviewType]}/${item.entityId}`}
                    className="font-serif text-2xl leading-tight text-primary-800 hover:underline"
                  >
                    {item.entityName}
                  </Link>
                  <span className="text-xs uppercase tracking-wide text-stone-500">
                    {ENTITY_LABEL[item.reviewType]}
                  </span>
                </div>
                <ReviewCard
                  review={item.review as AnySerializedReview}
                  reviewType={item.reviewType}
                  showActions={false}
                />
              </article>
            ))}
          </div>
        )}
      </PageContainer>
    </div>
  );
};
